import productsStore from "./products.store.js";
import recommendationsStore from "./recommendations.store.js";

const CLEAR_REFINEMENT = "CLEAR_REFINEMENT";
const REFINEMENT_LIST = "REFINEMENT_LIST";
const CURRENT_REFINEMENTS = "CURRENT_REFINEMENTS";

class CustomURLSearchParams extends URLSearchParams {
  /**
   * Since has with value is only supported with node 20,
   * so we have to add it here
   *
   * @param {string} name
   * @param {string} [value]
   * @returns {boolean}
   */
  has(name, value) {
    return value ? super.getAll(name).includes(value) : super.has(name);
  }
}

/**
 *
 * @param {Record<string, string> |  string[][] | string | URLSearchParams} [init]
 * @returns {URLSearchParams}
 */
function getURLSearchParams(init) {
  return new CustomURLSearchParams(init);
}

function searchResultTransformer(data) {
  data = JSON.parse(data);

  const results = (data?.results ?? []).map(result => ({
    ...result,
    id: result.uid,
    title: result.title.replace(/&amp;/g, "&"),
    options: JSON.parse(result.options.replace(/&quot;/g, '"')),
    variants: JSON.parse(result.variants.replace(/&quot;/g, '"')),
    color_swatches: JSON.parse(result.color_swatches.replace(/&quot;/g, '"')),
    metafields: JSON.parse(result.metafields.replace(/&quot;/g, '"')),
  }));

  return { ...data, results };
}

export default ({ app }, inject) => {
  app.store.registerModule("productsSearch", productsStore);
  app.store.registerModule("recommendations", recommendationsStore);
  const siteId = "g28s7j";

  /**
   * @param {Object} options
   * @param {string} options.queryPath
   * @param {number} options.pageSize
   * @param {string} options.collection
   * @param {Record<string, string>} options.facetKeyValues
   */
  function start({ queryPath, pageSize, collection, facetKeyValues = [] }) {
    const searchParams = getURLSearchParams(queryPath);
    let bgfacets = collection ? [["bgfilter.collections", collection]] : [];

    const targetPage =
      Number(searchParams.get("page")) ||
      Number(app?.context?.route?.query?.page) ||
      Number(app?.router?.currentRoute?.query?.page) ||
      1;

    facetKeyValues.forEach(({ key, value }) => {
      const filterKey = `filter.${key}`;
      if (!searchParams.has(filterKey, value)) {
        searchParams.append(filterKey, value);
      }
    });

    app.store.dispatch("productsSearch/initState", {
      pageSize,
      bgfacets,
      queryParams: [...searchParams.entries()],
    });

    return (async () => {
      const pages = {};

      try {
        const { data: firstPage } = await _search({ page: 1 });
        pages[1] = firstPage;

        if (targetPage > 1) {
          for (let p = 2; p <= targetPage; p++) {
            try {
              const { data } = await _search({ page: p });
              pages[p] = data;
            } catch (error) {
              break; // fail-safe — partial data still returned
            }
          }
        }
      } catch (err) {
        // silent fail-safe (store handles null cases)
      }

      return pages;
    })();
  }

  /**
   *
   * @param {CLEAR_REFINEMENT|REFINEMENT_LIST|CURRENT_REFINEMENTS|INFINITE_HITS} widget
   * @param {Object} facetOrFilter
   * @param {string} facetOrFilter.field
   * @param {string|{rangeHigh: string, rangeLow: str}} facetOrFilter.value
   * @param {string} facetOrFilter.multiple
   */
  async function refine(widget, { field, value: fieldValue, multiple } = {}) {
    let value = fieldValue;
    if (value?.rangeHigh && value?.rangeLow) {
      value = [value.rangeLow, value.rangeHigh];
    }

    const facetParams = _getQueryParams(widget, { field, multiple, value });
    app.store.dispatch("productsSearch/setFacetParams", { facetParams });
    app.store.dispatch("productsSearch/setPage", { page: 1 });

    const response = await _search();

    app.store.dispatch("productsSearch/updateURLQuery");

    return response;
  }

  async function showMore(page) {
    const response = await _search({ page });

    app.store.dispatch("productsSearch/setPage", { page });
    app.store.dispatch("productsSearch/updateURLQuery");

    return response;
  }

  function showPrevious(page) {
    return _search({ page });
  }

  async function sort({ field, direction }) {
    app.store.dispatch("productsSearch/setPage", { page: 1 });
    app.store.dispatch("productsSearch/setSortingParams", { field, direction });

    const response = await _search();
    app.store.dispatch("productsSearch/updateURLQuery");

    return response;
  }

  /**
   * @param {CLEAR_REFINEMENT|REFINEMENT_LIST|CURRENT_REFINEMENTS|INFINITE_HITS} widget
   * @param {Object} facetOrFilter
   * @param {string} facetOrFilter.field
   * @param {string|{rangeHigh: string, rangeLow: str}} facetOrFilter.value
   * @param {string} facetOrFilter.multiple
   */
  function createURL(widget, { field, value: fieldValue, multiple } = {}) {
    let value = fieldValue;
    if (value?.rangeHigh && value?.rangeLow) {
      value = [value.rangeLow, value.rangeHigh];
    }

    const queryParams = _getQueryParams(widget, { field, value, multiple });
    const searchState = app.store.state.productsSearch;

    return getURLSearchParams([
      ["query", searchState.query],
      ...searchState.sortingParams,
      ...searchState.otherParams,
      ...queryParams,
    ]).toString();
  }

  /**
   *
   * @param {CLEAR_REFINEMENT|REFINEMENT_LIST|CURRENT_REFINEMENTS} widget
   * @param {Object} option
   * @param {string} option.field
   * @param {string|string[]} option.value
   * @param {"single"|"multiple"} option.multiple
   */
  function _getQueryParams(
    widget,
    { field, value, multiple = "multiple" } = {}
  ) {
    const filterKey = `filter.${field}`;
    const searchState = app.store.state.productsSearch;

    if (widget === CLEAR_REFINEMENT) return [];

    if (widget === CURRENT_REFINEMENTS) {
      return [...searchState.facetParams].filter(
        ([key, facetValue]) =>
          !(key === filterKey && value === facetValue) &&
          !(
            Array.isArray(value) &&
            (key === `${filterKey}.low` || key === `${filterKey}.high`)
          )
      );
    }

    if (widget === REFINEMENT_LIST) {
      let params = getURLSearchParams(searchState.facetParams);

      if (Array.isArray(value) && value.length === 2) {
        const [low, high] = value;
        const lowKey = `${filterKey}.low`;
        const highKey = `${filterKey}.high`;

        if (params.has(lowKey, low) && params.has(highKey, high)) {
          params.delete(lowKey);
          params.delete(highKey);
        } else {
          params.set(lowKey, low);
          params.set(highKey, high);
        }

        return [...params.entries()];
      }

      if (params.has(filterKey, value)) {
        params.delete(filterKey, value);
      } else {
        multiple === "single"
          ? params.set(filterKey, value)
          : params.append(filterKey, value);
      }

      return [...params.entries()];
    }
  }

  /**
   *
   * @param {Object} options
   * @param {string|number} options.page
   */
  async function _search({ page } = {}) {
    app.store.dispatch("productsSearch/setLoading", true);
    const searchState = app.store.state.productsSearch;

    let params = getURLSearchParams([
      ["page", page ?? searchState.page],
      ["resultsPerPage", searchState.pageSize],
      ["siteId", siteId],
      // Enables Beacon v2 search reporting by returning responseId
      ["beacon", "true"],
      ["resultsFormat", "json"],
      ...searchState.bgfacetParams,
      ...searchState.facetParams,
      ...searchState.sortingParams,
      ...Object.entries({
        ...app.$search.getUserTrackingParams(),
        ...app.$search.getPersonalizationParams(),
      }),
    ]);

    if (searchState.query) params.set("q", searchState.query);

    const url = `https://${siteId}.a.searchspring.io/api/search/search.json?${params}`;

    try {
      return await app.$axios.get(url, {
        transformResponse: [searchResultTransformer],
      });
    } catch (err) {
      app.$bugsnag.notify(err);
      throw err;
    } finally {
      await new Promise(resolve => setTimeout(resolve, 500));
      app.store.dispatch("productsSearch/setLoading", false);
    }
  }

  inject("productsSearch", {
    sort,
    start,
    refine,
    createURL,
    showMore,
    showPrevious,
  });
};
